import { useState, FormEvent, useEffect, useCallback } from 'react';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuoteModal({ isOpen, onClose }: QuoteModalProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Reset status when modal opens
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setErrorMessage('');
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    setStatus('loading');
    setErrorMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9pkt8di2vhsdh2d36h0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // Not JSON
      }

      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || (parsed?.message as string)
        || (parsed?.meta as Record<string, unknown>)?.detail as string
        || responseText;

      if (response.ok && (parsed as Record<string, unknown>)?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(String(serverMsg || 'Ocurrió un error al enviar el mensaje. Intente nuevamente.'));
      }
    } catch {
      setStatus('error');
      setErrorMessage('Error de conexión. Por favor verifique su internet e intente nuevamente.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />

      {/* Modal panel */}
      <div className="relative w-full sm:w-[90%] sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] bg-background-50 rounded-t-xl sm:rounded-xl border border-background-200/60 shadow-2xl overflow-y-auto animate-slideUp sm:animate-none">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 bg-background-50 border-b border-background-200/60">
          <h3 className="font-heading text-base sm:text-lg font-semibold text-foreground-950">
            {status === 'success' ? '¡Listo!' : 'Solicitar Cotización'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-lg text-foreground-600" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-5">
          {status === 'success' ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className="ri-check-line text-3xl" />
              </div>
              <h4 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                ¡Mensaje Enviado con Éxito!
              </h4>
              <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                Gracias por contactarnos. Un especialista de RESINVENCA se comunicará con usted en las próximas 24 horas.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setStatus('idle')}
                  className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Enviar Otra Cotización
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-semibold text-sm hover:bg-background-200 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Cerrar
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="space-y-4"
              noValidate
            >
              {/* Honeypot */}
              <div className="contact-hp-field">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div>
                <label htmlFor="modal-name" className="block text-foreground-700 text-xs font-medium mb-1.5">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="modal-name"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="Ing. Juan Pérez"
                />
              </div>

              <div>
                <label htmlFor="modal-email" className="block text-foreground-700 text-xs font-medium mb-1.5">
                  Correo Electrónico *
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="jperez@empresa.com"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="modal-company" className="block text-foreground-700 text-xs font-medium mb-1.5">
                    Empresa *
                  </label>
                  <input
                    type="text"
                    id="modal-company"
                    name="company"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="Nombre de su empresa"
                  />
                </div>
                <div>
                  <label htmlFor="modal-phone" className="block text-foreground-700 text-xs font-medium mb-1.5">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    id="modal-phone"
                    name="phone"
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="+58 000 000-0000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="modal-interest" className="block text-foreground-700 text-xs font-medium mb-1.5">
                  Producto o Servicio de Interés
                </label>
                <select
                  id="modal-interest"
                  name="interest"
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all cursor-pointer"
                >
                  <option value="">Seleccione una opción...</option>
                  <option value="Demulsificante para Crudo">Demulsificante para Crudo</option>
                  <option value="Cotización General">Cotización General</option>
                </select>
              </div>

              <div>
                <label htmlFor="modal-message" className="block text-foreground-700 text-xs font-medium mb-1.5">
                  Mensaje *
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                  placeholder="Describa sus requerimientos, volúmenes estimados..."
                />
                <p className="text-foreground-400 text-xs mt-1">Máximo 500 caracteres</p>
              </div>

              {status === 'error' && errorMessage && (
                <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-sm" />
                  </div>
                  <span>{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full px-6 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {status === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Enviar Solicitud
                    <i className="ri-send-plane-line" />
                  </span>
                )}
              </button>

              <p className="text-foreground-400 text-xs text-center">
                Sus datos están seguros. No compartimos información con terceros.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}